/**
 * AudioContextManager
 *
 * Manages the Web Audio API AudioContext lifecycle.
 * Handles creation, suspension, resumption, sample rate configuration,
 * and output device selection. All audio in the engine flows through
 * a single AudioContext managed here.
 *
 * @module @engine/audio/AudioContextManager
 */

import type { AudioContextConfig } from './types';

export interface AudioContextManagerOptions extends Partial<AudioContextConfig> {}

const DEFAULT_CONFIG: AudioContextConfig = {
  sampleRate: 0,
  latencyHint: 'interactive',
  maxVoices: 64,
  defaultPanningModel: 'HRTF',
  defaultDistanceModel: 'inverse',
  sinkId: '',
};

/**
 * Singleton manager for the Web Audio API AudioContext.
 *
 * Provides full control over context lifecycle, sample rate,
 * latency hints, and output device routing.
 */
export class AudioContextManager {
  private _ctx: AudioContext | null = null;
  private _config: AudioContextConfig;
  private _masterGain: GainNode | null = null;
  private _analyser: AnalyserNode | null = null;
  private _audioElement: HTMLAudioElement | null = null;
  private _mediaElementSource: MediaElementAudioSourceNode | null = null;

  constructor(options: AudioContextManagerOptions = {}) {
    this._config = { ...DEFAULT_CONFIG, ...options };
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /** Get the current AudioContext (creates if needed) */
  get context(): AudioContext {
    if (!this._ctx) {
      this._createContext();
    }
    return this._ctx!;
  }

  /** Get master gain node */
  get masterGain(): GainNode {
    if (!this._masterGain) {
      this._masterGain = this.context.createGain();
      this._masterGain.connect(this.context.destination);
    }
    return this._masterGain;
  }

  /** Get analyser node for visualization */
  get analyser(): AnalyserNode {
    if (!this._analyser) {
      this._analyser = this.context.createAnalyser();
      this._analyser.fftSize = 2048;
      this._analyser.smoothingTimeConstant = 0.8;
      // Insert analyser between master gain and destination
      this.masterGain.disconnect();
      this.masterGain.connect(this._analyser);
      this._analyser.connect(this.context.destination);
    }
    return this._analyser;
  }

  /** Current AudioContext state */
  get state(): AudioContextState {
    return this._ctx?.state ?? 'closed';
  }

  /** Current sample rate */
  get sampleRate(): number {
    return this._ctx?.sampleRate ?? (this._config.sampleRate || 44100);
  }

  /** Base latency in seconds */
  get baseLatency(): number {
    return this._ctx?.baseLatency ?? 0;
  }

  /** Output latency in seconds */
  get outputLatency(): number {
    return (this._ctx as unknown as { outputLatency?: number })?.outputLatency ?? 0;
  }

  /** Current time of the audio context */
  get currentTime(): number {
    return this._ctx?.currentTime ?? 0;
  }

  /** Channel count of the destination */
  get channelCount(): number {
    return this._ctx?.destination.channelCount ?? 2;
  }

  /** Current config */
  get config(): Readonly<AudioContextConfig> {
    return this._config;
  }

  /**
   * Initialize the audio context.
   * Must be called in response to a user gesture on some browsers.
   */
  async initialize(): Promise<void> {
    if (this._ctx && this._ctx.state !== 'closed') {
      return;
    }
    this._createContext();
    await this.resume();
  }

  /**
   * Resume (unpause) the AudioContext.
   * Required after user gesture on many browsers.
   */
  async resume(): Promise<void> {
    if (this._ctx && this._ctx.state === 'suspended') {
      await this._ctx.resume();
    }
  }

  /**
   * Suspend the AudioContext to save CPU.
   */
  async suspend(): Promise<void> {
    if (this._ctx && this._ctx.state === 'running') {
      await this._ctx.suspend();
    }
  }

  /**
   * Close and destroy the AudioContext.
   */
  async close(): Promise<void> {
    if (this._ctx && this._ctx.state !== 'closed') {
      await this._ctx.close();
    }
    this._ctx = null;
    this._masterGain = null;
    this._analyser = null;
    this._mediaElementSource = null;
  }

  /**
   * Change the sample rate. Requires recreating the context.
   */
  async setSampleRate(sampleRate: AudioContextConfig['sampleRate']): Promise<void> {
    this._config.sampleRate = sampleRate;
    await this._recreateContext();
  }

  /**
   * Change the latency hint. Requires recreating the context.
   */
  async setLatencyHint(hint: AudioContextConfig['latencyHint']): Promise<void> {
    this._config.latencyHint = hint;
    await this._recreateContext();
  }

  /**
   * Set the output audio device.
   * Uses the Audio element's setSinkId() API when available.
   */
  async setOutputDevice(sinkId: string): Promise<void> {
    this._config.sinkId = sinkId;

    // Use the new AudioContext.setSinkId if available
    const ctx = this._ctx as unknown as { setSinkId?: (id: string) => Promise<void> };
    if (ctx?.setSinkId) {
      await ctx.setSinkId(sinkId);
      return;
    }

    // Fallback: use the HTML audio element's sinkId
    if (this._audioElement) {
      const el = this._audioElement as unknown as { setSinkId?: (id: string) => Promise<void> };
      if (el.setSinkId) {
        await el.setSinkId(sinkId);
      }
    }
  }

  /**
   * Get available output devices.
   */
  async getOutputDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(d => d.kind === 'audiooutput');
    } catch {
      return [];
    }
  }

  /**
   * Link an HTML <audio> element to this context.
   * All engine audio goes through Web Audio nodes, but having a single
   * <audio> element allows output device selection via setSinkId().
   */
  attachAudioElement(element: HTMLAudioElement): void {
    this._audioElement = element;
    if (this._ctx && !this._mediaElementSource) {
      try {
        this._mediaElementSource = this._ctx.createMediaElementSource(element);
        // The media element source is not connected to anything by default;
        // it exists purely so the audio element can route to the selected sinkId.
        // All actual audio goes through the AudioContext's destination.
      } catch {
        // Element may already be connected to a context
      }
    }
  }

  /**
   * Update config without recreating context (for non-destructive settings).
   */
  updateConfig(partial: Partial<AudioContextConfig>): void {
    Object.assign(this._config, partial);
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  private _createContext(): void {
    const options: AudioContextOptions = {
      latencyHint: this._config.latencyHint,
    };
    if (this._config.sampleRate > 0) {
      options.sampleRate = this._config.sampleRate;
    }
    this._ctx = new AudioContext(options);

    // Create master gain
    this._masterGain = this._ctx.createGain();
    this._masterGain.connect(this._ctx.destination);

    // Apply output device if set
    if (this._config.sinkId) {
      const ctx = this._ctx as unknown as { setSinkId?: (id: string) => Promise<void> };
      if (ctx.setSinkId) {
        ctx.setSinkId(this._config.sinkId).catch(() => {
          // Ignore device selection errors
        });
      }
    }
  }

  private async _recreateContext(): Promise<void> {
    const wasRunning = this._ctx?.state === 'running';
    await this.close();
    this._createContext();
    if (wasRunning) {
      await this.resume();
    }
  }
}
